import React, { useEffect, useState } from "react";
import {
  Button,
  Breadcrumb,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row,
  Stack,
  Table,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import PubSub from "pubsub-js";
import { ROUTES } from "../../constants/webRouting";
import fuziElevatorsApi from "../../api/fuziElevatorsApi";
import { getUserDetails } from "../../utils/helper";

// ── Constants ─────────────────────────────────────────────────
const ELEVATOR_TYPE_OPTIONS = [
  "Passenger",
  "Passenger cum Goods",
  "Passenger cum Stretcher",
  "Passenger cum Bed",
  "Goods",
].map((v) => ({ label: v, value: v }));

const BUILDING_TYPE_OPTIONS = [
  "Villa",
  "Residential",
  "Commercial",
  "Retail",
  "Hospital",
  "Hotel",
  "Infra",
  "School",
  "College",
  "Temple",
].map((v) => ({ label: v, value: v }));
const MOTOR_OPTIONS = [
  "Vacuum",
  "Geared",
  "Gearless",
  "Hydraulic",
  "Wittur",
].map((v) => ({ label: v, value: v }));
const CABIN_FINISH_OPTIONS = [
  "Mild Steel",
  "Stainless Steel",
  "Golden",
  "Rose Gold",
].map((v) => ({ label: v, value: v }));
const DOOR_VISION_OPTIONS = [
  "Non Vision",
  "Small Vision",
  "Big Vision",
  "Full Vision",
].map((v) => ({ label: v, value: v }));

const DOOR_OPENING_OPTIONS = [
  "Center Opening",
  "Side Opening - Left Hand Side",
  "Side Opening - Right Hand Side",
].map((v) => ({ label: v, value: v }));

// Opening side options for each floor stop
const OPENING_SIDE_OPTIONS = [
  { label: "Front", value: "Front" },
  { label: "Rear", value: "Rear" },
  { label: "Both (Front + Rear)", value: "Both" },
];

// Default floor names for the stops table
const DEFAULT_FLOOR_NAMES = [
  "Basement 3",
  "Basement 2",
  "Basement 1",
  "Basement",
  "Ground Floor",
  "First Floor",
  "Second Floor",
  "Third Floor",
  "Fourth Floor",
  "Fifth Floor",
];

/**
 * Build default line_items (stops) from a given count.
 * Each stop: { floor_name, opening_side, height_mm, sort_order }
 */
const buildDefaultStops = (count = 5) =>
  Array.from({ length: Math.min(Math.max(count, 2), 10) }, (_, i) => ({
    floor_name: DEFAULT_FLOOR_NAMES[i] || `Floor ${i + 1}`,
    opening_side: "Front",
    height_mm: "",
    sort_order: i + 1,
  }));

const toSelectValues = (str) => {
  if (!str || str.trim() === "") return [];
  return str.split(",").map((v) => ({ label: v.trim(), value: v.trim() }));
};

// Compute total openings from stops
const calcTotalOpenings = (stops) =>
  stops.reduce((sum, s) => sum + (s.opening_side === "Both" ? 2 : 1), 0);

// ── Component ──────────────────────────────────────────────────
const SiteVisitReportEdit = () => {
  const userInfo = getUserDetails();
  const location = useLocation();
  const navigate = useNavigate();

  const existingReport = location.state?.id ? location.state : {};
  const OPENING_VALUES = ["Front", "Rear", "Both"];

  const migrateLineItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return items.map((item, i) => {
      const isNewMapping = OPENING_VALUES.includes(item.floor_from);
      return {
        id: item.id,
        floor_name: isNewMapping
          ? item.floor_to || "Floor " + (i + 1)
          : item.floor_to || item.floor_from || "Floor " + (i + 1),
        opening_side: isNewMapping ? item.floor_from : "Front",
        height_mm: item.height_mm || "",
        sort_order: item.sort_order || i + 1,
      };
    });
  };

  const initialStops =
    migrateLineItems(location.state?.line_items) ||
    buildDefaultStops(existingReport.total_stops || 5);

  const [report, setReport] = useState(existingReport);
  const [lineItems, setLineItems] = useState(initialStops);
  const [leads, setLeads] = useState([]);
  const [validated, setValidated] = useState(false);
  const [assignUsers, setAssignUsers] = useState([]);

  // ── Load users ─────────────────────────────────────────────
  useEffect(() => {
    async function loadUsers() {
      const users = await fuziElevatorsApi.fetchUsers();
      if (Array.isArray(users)) setAssignUsers(users);
    }
    loadUsers();
  }, []);

  useEffect(() => {
    if (assignUsers.length === 0) return;
    if (!report.owner_ids || report.owner_ids.length === 0) return;
    if (typeof report.owner_ids[0] === "object" && report.owner_ids[0]?.value)
      return;

    const converted = report.owner_ids
      .map((id) => {
        const user = assignUsers.find((u) => u.id === id);
        return user ? { value: user.id, label: user.username } : null;
      })
      .filter(Boolean);

    if (converted.length > 0) {
      setReport((prev) => ({ ...prev, owner_ids: converted }));
    }
  }, [assignUsers]);

  // ── Load leads ──────────────────────────────────────────────
  useEffect(() => {
    async function init() {
      const result = await fuziElevatorsApi.fetchLeads({});
      if (Array.isArray(result)) {
        setLeads(
          result.map((l) => ({
            value: l.id,
            label: `${l.enquiry_no} - ${l.lead_name}`,
            phone: l.phone,
            whatsapp_no: l.whatsapp_no,
            address: l.address,
            customer_name: l.lead_name,
            owner_ids: l.owner_ids || [],
          })),
        );
      }
    }
    init();
  }, []);

  // ── Handlers ────────────────────────────────────────────────
  const handleLeadSelect = (selected) => {
    if (!selected) return;
    setReport((prev) => ({
      ...prev,
      lead_id: selected.value,
      enquiry_no: selected.label,
      customer_name: selected.customer_name,
      phone: selected.phone,
      whatsapp_no: selected.whatsapp_no,
      site_address: selected.address,
      owner_ids: Array.isArray(selected.owner_ids)
        ? selected.owner_ids.filter((item) => item?.value)
        : [],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReport((prev) => ({ ...prev, [name]: value }));
  };

  // ── Stop (floor) handlers ────────────────────────────────────
  const handleStopChange = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  /**
   * When total_stops changes: rebuild the stops list.
   * Preserve existing data for rows that already exist.
   */
  const handleStopsCountChange = (e) => {
    const newCount = Math.min(
      Math.max(parseInt(e.target.value, 10) || 2, 2),
      10,
    );
    setReport((prev) => ({ ...prev, total_stops: newCount }));

    setLineItems((prev) => {
      const newItems = Array.from({ length: newCount }, (_, i) => {
        if (prev[i]) return { ...prev[i], sort_order: i + 1 };
        return {
          floor_name: DEFAULT_FLOOR_NAMES[i] || `Floor ${i + 1}`,
          opening_side: "Front",
          height_mm: "",
          sort_order: i + 1,
        };
      });
      return newItems;
    });
  };

  const addStop = () => {
    if (lineItems.length >= 10) return;
    const nextIndex = lineItems.length;
    setLineItems((prev) => [
      ...prev,
      {
        floor_name: DEFAULT_FLOOR_NAMES[nextIndex] || `Floor ${nextIndex + 1}`,
        opening_side: "Front",
        height_mm: "",
        sort_order: nextIndex + 1,
      },
    ]);
    setReport((prev) => ({ ...prev, total_stops: lineItems.length + 1 }));
  };

  const removeStop = (index) => {
    if (lineItems.length <= 2) return; // minimum 2
    setLineItems((prev) => {
      const updated = prev
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, sort_order: i + 1 }));
      setReport((r) => ({ ...r, total_stops: updated.length }));
      return updated;
    });
  };

  const handleCancel = () => {
    if (report.id) {
      navigate(`${ROUTES.SITE_VISIT_REPORT.LIST}/${report.id}`, {
        state: report,
      });
    } else {
      navigate(ROUTES.SITE_VISIT_REPORT.LIST);
    }
  };

  // ── Submit ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!report.lead_id) {
      setValidated(true);
      return PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Validation Error",
        message: "Please select a Lead / Enquiry",
      });
    }

    // Map UI fields -> DB columns before saving:
    //   floor_name  -> floor_to   (DB column)
    //   opening_side -> floor_from (DB column)
    const finalStops = lineItems
      .filter((item) => item.floor_name?.trim())
      .map((item) => ({
        id: item.id,
        floor_to: item.floor_name, // floor name stored in floor_to
        floor_from: item.opening_side, // opening side stored in floor_from
        height_mm: item.height_mm || null,
        sort_order: item.sort_order,
      }));
    const totalOpenings = calcTotalOpenings(lineItems);

    const payload = {
      ...report,
      // overhead_height: report.overhead_height || null,
      overhead_height: (report.overhead_height === "" || report.overhead_height === undefined) 
      ? null 
      : report.overhead_height,
      total_stops: finalStops.length,
      number_of_openings: totalOpenings,
      line_items: finalStops,
      owner_ids: Array.isArray(report.owner_ids)
        ? report.owner_ids
            .filter((item) => item?.value)
            .map((item) => item.value)
        : [],
    };

    try {
      let result;
      if (payload.id) {
        result = await fuziElevatorsApi.saveSiteVisitReport(payload);
        if (result?.success) {
          await fuziElevatorsApi.saveSiteVisitReportFloors(
            payload.id,
            finalStops,
          );
        }
      } else {
        result = await fuziElevatorsApi.createSiteVisitReport(payload);
      }

      if (result?.success || result?.id) {
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Success",
          message: "Report saved successfully",
        });
        const finalId = result?.data?.id || result?.id || payload.id;
        navigate(`${ROUTES.SITE_VISIT_REPORT.LIST}/${finalId}`);
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message: result?.errors || "Failed to save",
        });
      }
    } catch (error) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Failed to save",
      });
    }
  };

  // ── Derived display ─────────────────────────────────────────
  const totalOpenings = calcTotalOpenings(lineItems);

  // ── Render ──────────────────────────────────────────────────
  return (
    <Container className="pb-5">
      <Row>
        <Col md={12} className="sticky-breadcrumb">
          <Breadcrumb>
            <Breadcrumb.Item onClick={() => navigate(ROUTES.HOME)}>
              Home
            </Breadcrumb.Item>
            <Breadcrumb.Item
              onClick={() => navigate(ROUTES.SITE_VISIT_REPORT.LIST)}
            >
              Site Visit Reports
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>

        <Col md={12}>
          <Form onSubmit={handleSubmit} noValidate validated={validated}>
            <Card className="shadow">
              <CardHeader className="bg-custom text-white sticky-card-header">
                <Row className="w-100">
                  <Col lg={6}>
                    <div className="d-flex align-items-center h-100">
                      <h5 className="m-0">
                        {report.id
                          ? "Edit Site Visit Report"
                          : "Create Site Visit Report"}
                      </h5>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <Stack direction="horizontal" className="float-end" gap={2}>
                      <Button size="sm" className="btn-save" type="submit">
                        Save
                      </Button>
                      <Button
                        size="sm"
                        className="btn-cancel"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Row className="view-form">
                  {/* ── Basic Info ───────────────────────── */}
                  <Col md={12} className="section-header">
                    Basic Information
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Enquiry / Lead
                      </Form.Label>
                      <Select
                        placeholder="Select Enquiry"
                        options={leads}
                        value={
                          report.lead_id
                            ? leads.find((l) => l.value === report.lead_id)
                            : null
                        }
                        onChange={handleLeadSelect}
                      />
                      {validated && !report.lead_id && (
                        <div className="feedback-invalid">
                          Enquiry is required
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Elevator Type
                      </Form.Label>
                      <Select
                        placeholder="Select Elevator Type"
                        options={ELEVATOR_TYPE_OPTIONS}
                        value={
                          report.elevator_type
                            ? {
                                label: report.elevator_type,
                                value: report.elevator_type,
                              }
                            : null
                        }
                        onChange={(e) =>
                          setReport((p) => ({ ...p, elevator_type: e.value }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Customer Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="customer_name"
                        placeholder="Enter Customer Name"
                        value={report.customer_name || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Address
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="site_address"
                        placeholder="Enter Site Address"
                        value={report.site_address || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Mobile No.
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        placeholder="Enter Mobile No."
                        value={report.phone || ""}
                        onChange={handleChange}
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Person Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="site_person_name"
                        placeholder="Enter site person name"
                        value={report.site_person_name || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Person Mobile
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="site_person_mobile"
                        placeholder="Enter site person mobile"
                        value={report.site_person_mobile || ""}
                        onChange={handleChange}
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Assigned Users
                      </Form.Label>
                      <Select
                        isMulti
                        placeholder="Select Assigned Users"
                        options={assignUsers.map((user) => ({
                          value: user.id,
                          label: user.username,
                        }))}
                        value={
                          Array.isArray(report.owner_ids)
                            ? report.owner_ids.filter((item) => item?.value)
                            : []
                        }
                        onChange={(selected) =>
                          setReport((prev) => ({
                            ...prev,
                            owner_ids: selected || [],
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Technical Info ───────────────────── */}
                  <Col md={12} className="section-header">
                    Technical Information
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Pit Available (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="lift_pit"
                        placeholder="Enter pit size in mm"
                        value={report.lift_pit || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Machine Room Available
                      </Form.Label>
                      <Form.Select
                        name="machine_room"
                        value={report.machine_room || ""}
                        onChange={handleChange}
                      >
                        <option value="">---Select---</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/*
                    Number of Stops: driven by the floor table below (2–10).
                    Number of Openings: auto-calculated from opening_side selections.
                  */}
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Number of Stops{" "}
                        <small className="text-muted">(min 2, max 10)</small>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="total_stops"
                        min={2}
                        max={10}
                        placeholder="Enter number of stops"
                        value={report.total_stops || lineItems.length}
                        onChange={handleStopsCountChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Number of Openings{" "}
                        <small className="text-muted">(auto-calculated)</small>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        readOnly
                        value={totalOpenings}
                        className="bg-light"
                        title="Calculated from floor opening sides below"
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Building Type
                      </Form.Label>
                      <Select
                        placeholder="Select building type"
                        options={BUILDING_TYPE_OPTIONS}
                        value={
                          report.segment
                            ? { label: report.segment, value: report.segment }
                            : null
                        }
                        onChange={(e) =>
                          setReport((p) => ({ ...p, segment: e.value }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Motor Type
                      </Form.Label>
                      <Select
                        isMulti
                        placeholder="Select motor type"
                        options={MOTOR_OPTIONS}
                        value={toSelectValues(report.motor)}
                        onChange={(selected) =>
                          setReport((p) => ({
                            ...p,
                            motor: selected.map((s) => s.value).join(","),
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Door & Cabin ─────────────────────── */}
                  <Col md={12} className="section-header">
                    Door & Cabin Information
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Cabin Finish
                      </Form.Label>
                      <Select
                        isMulti
                        placeholder="Select cabin finish"
                        options={CABIN_FINISH_OPTIONS}
                        value={toSelectValues(report.cabin_metal)}
                        onChange={(selected) =>
                          setReport((p) => ({
                            ...p,
                            cabin_metal: selected.map((s) => s.value).join(","),
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Door Vision
                      </Form.Label>
                      <Select
                        isMulti
                        placeholder="Select door vision"
                        options={DOOR_VISION_OPTIONS}
                        value={toSelectValues(report.door_vision)}
                        onChange={(selected) =>
                          setReport((p) => ({
                            ...p,
                            door_vision: selected.map((s) => s.value).join(","),
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Door Opening
                      </Form.Label>
                      <Select
                        placeholder="Select door opening"
                        options={DOOR_OPENING_OPTIONS}
                        value={
                          report.door_opening
                            ? {
                                label: report.door_opening,
                                value: report.door_opening,
                              }
                            : null
                        }
                        onChange={(e) =>
                          setReport((p) => ({ ...p, door_opening: e.value }))
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Number of Door Panels
                      </Form.Label>
                      <Form.Select
                        name="door_panels"
                        value={report.door_panels || "2"}
                        onChange={handleChange}
                      >
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Door Width (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="door_width"
                        placeholder="Enter door width in mm"
                        value={report.door_width || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Door Height (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="door_height"
                        placeholder="Enter door height in mm"
                        value={report.door_height || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Car & Capacity ────────────────────── */}
                  <Col md={12} className="section-header">
                    Car Size & Capacity
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Car Width (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="car_internal_width"
                        placeholder="Enter car width in mm"
                        value={report.car_internal_width || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Car Depth (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="car_internal_depth"
                        placeholder="Enter car depth in mm"
                        value={report.car_internal_depth || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Capacity (Persons)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="passenger_capacity"
                        placeholder="Enter person capacity"
                        value={report.passenger_capacity || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Capacity (Kg)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="elevator_capacity"
                        placeholder="Enter kg capacity"
                        value={report.elevator_capacity || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Lift Well ──────────────────────────── */}
                  <Col md={12} className="section-header">
                    Lift Well Size
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Lift Well Width (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="shaft_width"
                        placeholder="Enter shaft width in mm"
                        value={report.shaft_width || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Lift Well Depth (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="shaft_debth"
                        placeholder="Enter shaft depth in mm"
                        value={report.shaft_debth || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  {/* ── NEW: Overhead Height ─────────────── */}
                  {/* <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Overhead Height (mm)
                      </Form.Label>
                      <Form.Control
                        type="number" 
                        name="overhead_height"
                        placeholder="Enter overhead height in mm"
                        // Ensure karein ki null/undefined hone par empty string dikhe
                        value={report.overhead_height ?? ""} 
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col> */}
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Overhead Height (mm)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="overhead_height"
                        placeholder="Enter overhead height in mm"
                        value={report.overhead_height ?? ""} 
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Plaster
                      </Form.Label>
                      <Form.Select
                        name="shaft_construction"
                        value={report.shaft_construction || ""}
                        onChange={handleChange}
                      >
                        <option value="">---Select---</option>
                        <option value="with plaster">With Plaster</option>
                        <option value="without plaster">Without Plaster</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* ── Visit Info ─────────────────────────── */}
                  <Col md={12} className="section-header">
                    Visit Information
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Visited By
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="engineer_name"
                        placeholder="Enter engineer name"
                        value={report.engineer_name || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Site Visit Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="site_visit_date"
                        value={report.site_visit_date || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className="form-view-label">
                        Remark
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="remark"
                        placeholder="Enter remark"
                        value={report.remark || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  {/* ── Floor Stops & Openings ──────────────────────────────── */}
                  <Col md={12} className="section-header">
                    Floor Stops & Door Openings
                    <small className="text-muted ms-2 fw-normal">
                      — Total openings (auto): <strong>{totalOpenings}</strong>
                      &nbsp;|&nbsp;Min 2 stops, Max 10 stops
                    </small>
                  </Col>

                  <Col lg={12}>
                    <Table bordered hover responsive size="sm">
                      <thead className="bg-custom text-white">
                        <tr>
                          <th style={{ width: "50px" }}>#</th>
                          <th>Floor Name</th>
                          <th style={{ width: "220px" }}>
                            Opening Side
                            <br />
                            <small className="fw-normal opacity-75">
                              "Both" counts as 2 openings
                            </small>
                          </th>
                          <th style={{ width: "140px" }}>Height (mm)</th>
                          <th style={{ width: "80px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((item, index) => (
                          <tr key={item.id || index}>
                            <td className="text-center align-middle">
                              {index + 1}
                            </td>

                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="e.g. Ground Floor"
                                value={item.floor_name || ""}
                                onChange={(e) =>
                                  handleStopChange(
                                    index,
                                    "floor_name",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td>
                              <Form.Select
                                size="sm"
                                value={item.opening_side || "Front"}
                                onChange={(e) =>
                                  handleStopChange(
                                    index,
                                    "opening_side",
                                    e.target.value,
                                  )
                                }
                              >
                                {OPENING_SIDE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>

                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                placeholder="mm"
                                value={item.height_mm || ""}
                                onChange={(e) =>
                                  handleStopChange(
                                    index,
                                    "height_mm",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="text-center align-middle">
                              <Button
                                variant="danger"
                                size="sm"
                                disabled={lineItems.length <= 2}
                                title={
                                  lineItems.length <= 2
                                    ? "Minimum 2 stops required"
                                    : "Remove stop"
                                }
                                onClick={() => removeStop(index)}
                              >
                                <i className="fa fa-trash-o" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {lineItems.length < 10 && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={addStop}
                      >
                        <i className="fa fa-plus" /> Add Stop (Max 10)
                      </Button>
                    )}
                    {lineItems.length >= 10 && (
                      <small className="text-muted">
                        Maximum 10 stops reached.
                      </small>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default SiteVisitReportEdit;